/**
 * InvoiceBilledBanner — informational alert for orgs on per-invoice
 * enterprise contracts (``billing_mode === "invoice"``).
 *
 * V1 reference: ``ando-frontend/app/components/billing/InvoiceBilledBanner.tsx``.
 * V1 used framer-motion to fade-in; V2 omits the animation on first paint
 * because the Billing settings surface is a static page (no list churn)
 * and the entry animation never had perceptible value.
 *
 * Render contract: ``null`` for every billing mode that ISN'T
 * ``"invoice"`` so the page consumer can drop the component in
 * unconditionally — no host-side gating boilerplate.
 */

import { Building2 } from "lucide-react";
import type * as React from "react";
import { Trans } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import type { BillingSubscriptionResponse } from "@ando/core";

export interface InvoiceBilledBannerProps {
  readonly billingMode: BillingSubscriptionResponse["billing_mode"] | undefined;
  readonly className?: string;
}

export const InvoiceBilledBanner: React.FC<InvoiceBilledBannerProps> = ({
  billingMode,
  className,
}) => {
  if (billingMode !== "invoice") return null;
  return (
    <aside
      role="status"
      aria-live="polite"
      className={cn(
        "flex items-start gap-3 rounded-lg border border-warning/30 bg-warning/[0.07] p-3",
        className,
      )}
    >
      <Building2
        className="mt-0.5 h-4 w-4 shrink-0 text-warning"
        aria-hidden
        strokeWidth={2}
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold text-[13px] text-ink-1">
          <Trans>Piano gestito a fattura</Trans>
        </p>
        <p className="text-[11.5px] text-ink-3 leading-relaxed">
          <Trans>Il tuo piano è governato dall'organizzazione tramite contratto enterprise.</Trans>
        </p>
        <p className="text-[11.5px] text-ink-3 leading-relaxed">
          <Trans>Per modifiche al piano o crediti aggiuntivi contatta l'amministratore della tua azienda o il referente commerciale.</Trans>
        </p>
      </div>
    </aside>
  );
};
InvoiceBilledBanner.displayName = "InvoiceBilledBanner";
